import { globals } from 'config/globals';
import HomeColours from 'app/views/home/config/HomeColours';
import HomeScreens from 'app/views/home/webgl/particles/scenes/HomeScreens';
import {
	BEHAVIOUR_BREATHING,
	BEHAVIOUR_DISABLE_TIMELINE_X,
	BEHAVIOUR_PROGRESS_PARALLAX,
	BEHAVIOUR_REPELL,
	FLAG_ACTIVE,
	REPELL_PUSH,
	VEC_X,
	VEC_Y,
	writeVectorConfig
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import { FRAME_RING, FRAME_SOLID2 } from 'app/views/home/webgl/particlesOptimised/core/frames';
import { hexToTint } from 'app/views/home/webgl/particlesOptimised/departments/departments.types';

import type { ImagePixelDataGrid } from 'utils/ImagePixelDataGrid';
import type { ParticleCore } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import type { DepartmentDeps } from 'app/views/home/webgl/particlesOptimised/departments/departments.types';

/**
 * ScreensRadialHalftone — FORK of scenes/RadialHalftoneImage.ts (+ its inner
 * RadialHalftoneParticle.create), re-expressed as lane writes over a screens
 * pool slice. One instance per radialHalftoneImage phase pass, like the
 * reference (points / init config / scale lookups reset per pass).
 *
 * Reference quirks kept bug-compatible:
 *  - createGrid RESETS _initConfig at its top, so of halftoneGrid's three
 *    channel passes only the LAST ('blue') survives — and create() is called
 *    TWICE by the department, so only the second sizing's blue grid renders.
 *    The earlier passes still run for their side effects (the bare
 *    getRandomColour draws, the dynamicSets/ambient-colour writes);
 *  - scaleLookups persist across createGrid calls but are only populated by
 *    processInitParticle (after both create() calls) — the `||` fallback in
 *    createGrid therefore always samples the image;
 *  - points whose clipped ratio lands outside the image get an undefined
 *    baseScale: the reference NaN-poisons their position Inertias (sprite.x
 *    = NaN -> invisible particle). The lanes NaN the same way — invisible on
 *    both sides (the reference's stale sprite-scale guard is moot once the
 *    position is NaN);
 *  - the per-sprite blendMode = 'add' becomes the blendAdd lane (the screens
 *    pool's SplitBlendParticleRenderer routes on it).
 */
export class ScreensRadialHalftone {
	private _core: ParticleCore;
	private _deps: DepartmentDeps;

	private _poolStart: number = 0;
	private _poolCount: number = 0;
	private _poolIndex: number = 0;

	public offsetX: number = 0;
	public offsetY: number = 0;
	public fillStyle: string = 'additive';

	private _scaleLookups: Array<Array<number>> = [];
	private _imageData: ImagePixelDataGrid;
	private _colourTarget: Array<string>;
	private _initConfig: Array<any> = [];
	//The live points (offY sweeps them) — lane + the create-time roll:
	private _points: Array<{ lane: number; baseScale: number }> = [];

	private ROOT_2 = Math.sqrt(2);
	private TAU = Math.PI * 2;

	private _channelFillStyles = {
		additive: {
			red: '#FF0000',
			green: '#00FF00',
			blue: '#0000FF',
			lum: '#FFF'
		},
		subtractive: {
			red: '#00FFFF',
			green: '#FF00FF',
			blue: '#FFFF00',
			lum: '#000'
		}
	};

	constructor(core: ParticleCore, deps: DepartmentDeps) {
		this._core = core;
		this._deps = deps;
	}

	public setImgData(imageData: ImagePixelDataGrid): void {
		this._imageData = imageData;
	}

	/** setPool(particlePool.slice(0, total)) — a lane slice here. */
	public setPool(start: number, count: number): void {
		this._poolStart = start;
		this._poolCount = count;
	}

	public create(w: number, h: number, gridSize: number): void {
		this._halftoneGrid(w, h, gridSize);
	}

	public tick(deltaTime: number): void {
		this._processInitParticle(deltaTime);
	}

	/** offY — the phase-out sweep: vector-y drift up to y 0, shrink, no repell. */
	public offY(): void {
		const core = this._core;

		for (let i = 0; i < this._points.length; i++) {
			const lane = this._points[i].lane;

			//animatedProps._y.setVectorConfig(true, 1, 10):
			writeVectorConfig(core, lane, VEC_Y, true, 1, 10);

			//setEase({x: 20, y: 20, scale: 30}):
			core.easeX[lane] = 20;
			core.easeY[lane] = 20;
			core.easeScale[lane] = 30;

			//stopBreathing():
			core.behaviourMask[lane] &= ~BEHAVIOUR_BREATHING;

			core.tScale[lane] = 0.1 + Math.random() * 0.2;
			core.behaviourMask[lane] &= ~BEHAVIOUR_REPELL;

			const xd = Math.random() < 0.5 ? -1 : 1;
			const xDiff = xd * (Math.random() * 0.01);
			//position.set (SOFT) — off the CURRENT rel position:
			core.relX[lane] = core.relX[lane] + xDiff;
			core.relY[lane] = 0;
		}
	}

	//Private
	//-----------------------------------------------------------------------------------------------

	private _halftoneGrid(w: number, h: number, gridSize: number, channels: Array<string> = ['red', 'green', 'blue']): void {
		Math.random(); //bare draw kept (reference side effect)

		//scaleCorrect dance is dead in the reference (immediately reset to 1).

		HomeColours.dynamicSets.halftone = [
			this._channelFillStyles[this.fillStyle].green,
			this._channelFillStyles[this.fillStyle].red
		];

		if (globals.colourPhase === 'yellow') this._colourTarget = HomeColours.dynamicSets.differ;
		else this._colourTarget = HomeColours.graduated;

		HomeScreens.setAmbientColours(this._colourTarget);

		this._poolIndex = 0;
		for (let i = 0; i < channels.length; i++) {
			this._createGrid(channels[i], w, h, gridSize);
		}
	}

	private _createGrid(channel: string, w: number, h: number, gridSize: number): void {
		const diag = Math.max(w, h) * this.ROOT_2;
		const angle = 0;
		const halfW = w / 2;
		const halfH = h / 2;
		const offset = gridSize;
		const centerX = halfW + Math.cos(angle) * offset;
		const centerY = halfH + Math.sin(angle) * offset;

		this._initConfig = [];

		const maxLevel = Math.ceil((diag + offset) / gridSize);

		//sceneProps.sizes.container.w/h — the virtual canvas ratio:
		const sizes = this._deps.getSizes();
		const yRatio = sizes.virtualWidth / sizes.innerHeight;

		for (let level = 0; level < maxLevel; level++) {
			const max = level * 6 || 1;

			if (!this._scaleLookups[level]) {
				this._scaleLookups[level] = [];
			}

			for (let j = 0; j < max; j++) {
				//Bare call kept for side-effect parity (result was never used):
				HomeColours.getRandomColour(this._colourTarget);

				const maxRadius = centerX + Math.cos(0) * (maxLevel - 1) * gridSize;

				const theta = (this.TAU * j) / max + angle;
				const x = centerX + Math.cos(theta) * level * gridSize;
				const yRaw = centerY + Math.sin(theta) * level * gridSize;
				const y = yRaw * yRatio;

				const newRangeRatioX = this._clipImageArea(maxRadius, w, x, 0.1, 0.1);
				const newRangeRatioY = this._clipImageArea(maxRadius, h, yRaw, 0.23, 0.23);

				const baseScale =
					this._scaleLookups[level][j] ||
					this._imageData.getPixelGreyByRatio(newRangeRatioX, newRangeRatioY, -0.1, -0.08);

				this._initConfig.push({
					channel: channel,
					x: x,
					y: y,
					baseScale: baseScale,
					level: level,
					j: j,
					counter: 1 + Math.random() * 8
				});
			}
		}
	}

	/** processInitParticle — staggered creation off the per-entry counters. */
	private _processInitParticle(deltaTime: number): void {
		let i = 0;

		while (i < this._initConfig.length) {
			this._initConfig[i].counter -= deltaTime;

			if (this._initConfig[i].counter <= 0) {
				const colour = HomeColours.getRandomColour(this._colourTarget);
				const level = this._initConfig[i].level;
				const j = this._initConfig[i].j;

				const point = this._initParticle(
					this._initConfig[i].channel,
					this._initConfig[i].x,
					this._initConfig[i].y,
					colour,
					this._initConfig[i].baseScale
				);

				if (point && !this._scaleLookups[level][j]) {
					this._scaleLookups[level][j] = point.baseScale;
				}

				this._initConfig.splice(i, 1);
			} else {
				i++;
			}
		}
	}

	private _initParticle(
		channel: string,
		x: number,
		y: number,
		colour: string,
		baseScale: number
	): { lane: number; baseScale: number } {
		//getNextHomeParticle — pool exhaustion returns undefined (config is
		//still consumed by the caller, like the reference):
		if (this._poolIndex >= this._poolCount) {
			this._poolIndex++;
			return null;
		}

		const lane = this._poolStart + this._poolIndex;
		this._poolIndex++;

		this._createPoint(lane, channel, x, y, colour, baseScale);

		const point = { lane: lane, baseScale: baseScale };
		this._points.push(point);
		return point;
	}

	/** RadialHalftoneParticle.create, lane-for-line. */
	private _createPoint(lane: number, channel: string, x: number, y: number, colour: string, baseScale: number): void {
		const core = this._core;

		//deactivateAllBehaviours(true): behaviours off, position stays; the
		//breathing/vector state on animatedProps SURVIVES it (re-armed below
		//anyway); disableTimelineX is position config and persists:
		core.behaviourMask[lane] =
			(core.behaviourMask[lane] & (BEHAVIOUR_BREATHING | BEHAVIOUR_DISABLE_TIMELINE_X)) | BEHAVIOUR_PROGRESS_PARALLAX;

		const offsets = {
			red: { x: 0.00025, y: 0.0015 },
			green: { x: 0.00025, y: -0.0015 },
			blue: { x: -0.00025, y: -0.0015 }
		};

		//hardSetScale(Math.random() * 1.2 * baseScale || 0.1, baseScale):
		const hard = Math.random() * 1.2 * baseScale || 0.1;
		core.tScale[lane] = hard;
		core.tScaleSaved[lane] = baseScale === undefined ? hard : baseScale;
		//animatedProps.scale = 0:
		core.scale[lane] = 0;

		//startBreathing(bs, 0.01*bs, 0.4*bs, [-1,1], rand*0.4*bs, ±1) —
		//argument evaluation order kept for the RNG stream:
		const startChangeValue = Math.random() * 0.4 * baseScale;
		const startDirection = Math.random() < 0.5 ? -1 : 1;
		core.breathBase[lane] = baseScale;
		core.breathChange[lane] = 0.01 * baseScale;
		core.breathMax[lane] = 0.4 * baseScale;
		core.breathAdd[lane] = startChangeValue * startDirection;
		core.breathDir[lane] = startDirection;
		core.behaviourMask[lane] |= BEHAVIOUR_BREATHING;

		//setVectorStyleMovement(true) — both axes, default rates:
		writeVectorConfig(core, lane, VEC_X, true);
		writeVectorConfig(core, lane, VEC_Y, true);

		//setEase({x: 20, y: 20}):
		core.easeX[lane] = 20;
		core.easeY[lane] = 20;

		//alpha = targetAlpha = 1; activate():
		core.alpha[lane] = 1;
		core.tAlpha[lane] = 1;
		core.stateFlags[lane] |= FLAG_ACTIVE;

		const px = x + this.offsetX + offsets[channel].x;
		const py = y + this.offsetY + offsets[channel].y;

		const startX = Math.random() * (0.008 * baseScale);
		//position.set({x + startX, y}, true) — HARD (disableTimelineX mode,
		//no xOffset on the hard path), then position.set({x, y}) — SOFT:
		const sizes = this._deps.getSizes();
		core.relX[lane] = px + startX;
		core.relY[lane] = py;
		core.tx[lane] = core.x[lane] = core.pTx[lane] = (px + startX) * sizes.virtualWidth;
		core.ty[lane] = core.y[lane] = core.pTy[lane] = py * sizes.innerHeight;
		core.relX[lane] = px;
		core.relY[lane] = py;

		core.tint[lane] = hexToTint(colour);

		//sprite.blendMode = 'add' -> the split renderer's additive batch:
		core.blendAdd[lane] = 1;

		//repell: PUSH 300 @ randomisePower(30, 200), screensMouse centre
		//(the facade's screens repell centre):
		core.behaviourMask[lane] |= BEHAVIOUR_REPELL;
		core.repellType[lane] = REPELL_PUSH;
		core.repellRadius[lane] = 300;
		core.repellPower[lane] = 30 + Math.random() * 200;

		const rand = Math.random();
		//rand < 0.0 (square) is dead; 4% rings:
		core.textureId[lane] = rand < 0.04 ? FRAME_RING : FRAME_SOLID2;
	}

	private _clipImageArea(maxCircleRadius: number, gridWidth: number, n: number, clip1: number = 0, clip2: number = 0): number {
		const range = maxCircleRadius * 2;
		const halfH = gridWidth / 2;
		const rangeCorrect = range - halfH * 2;
		const value = (n + maxCircleRadius) / range - rangeCorrect;

		if (!clip1 && !clip2) {
			return value;
		}

		const ratio = 1 - clip1 - clip2;
		const valueClipped = value - clip1;
		return valueClipped / ratio;
	}
}
